import type { JSX } from "react";
import React from "react";
import cn from "classnames";
import { useIntl } from "react-intl";

import { lengthenSolfa } from "../../common/parameters";
import { useUser } from "../store/user/selectors";

import { ITaskIconProps } from "./interfaces";
import { Icon, LogoMark, LogoSlogen, LogoWrapper } from "./styles";

const barSortingIcon = ({ active, completed }: ITaskIconProps) => (
    <Icon className={cn({ ActiveIcon: active, CompletedIcon: completed })}>
        <svg width="64" height="64" viewBox="0 0 64 64">
            <g>
                <path
                    d="M31.2,12.8l-6.1,6.1c8.5,8.5-.5,17.6-9,9L9.9,34.1,30.4,54.6,51.7,33.3l-8.9-8.9c7.8-.8,8.5-6.2,5.4-9.3s-8.5-1.5-9.3,5.4Z"
                    className={"color-1-fill"}
                />
                <line
                    x1="33"
                    y1="38.8"
                    x2="33.1"
                    y2="22"
                    className={"color-2-stroke"}
                    style={{ fill: "none", strokeMiterlimit: 10, strokeWidth: 0.64 }}
                />
                <path
                    className={"color-2-fill"}
                    d="M32.9,38.1c-.7-1.1-2.5-1.3-4.2-.4s-2.6,2.5-2,3.5,2.5,1.4,4.2.5S33.5,39.2,32.9,38.1Z"
                />
                <path
                    className={"color-2-fill"}
                    d="M33.1,22h.4a10,10,0,0,0,1.7,3.5c1.6,2.6,4.3,4,1.9,8.7,0,0-.1.3-.3.3s-.2-.1-.1-.3,2.7-5.6-3.2-7.5h-.4Z"
                />
            </g>
        </svg>
    </Icon>
);

const Icons: { [prop: string]: (props: any) => JSX.Element } = {
    Logo: ({ slogen = "", locale = "en" }) => (
        <LogoWrapper>
            <LogoMark />
            <LogoSlogen $centered={locale.slice(0, 2) === "en" || locale.slice(0, 2) === "zh" || locale === "nl"}>{slogen}</LogoSlogen>
        </LogoWrapper>
    ),

    barSorting: barSortingIcon,
    barSorting2: barSortingIcon,
    barline: ({ active, completed }: ITaskIconProps) => (
        <Icon className={cn({ ActiveIcon: active, CompletedIcon: completed })}>
            <svg width="64" height="64" viewBox="0 0 64 64">
                <rect className="color-1-fill" x="9.3" y="18.95" width="45.4" height="26.11" rx="2.33" />
                <polygon className="color-2-fill" points="24.46 26 24.46 38.27 23.46 38.27 23.46 28.42 13.97 28.42 13.97 38.27 12.97 38.27 12.97 26 24.46 26" />
                <rect className="color-2-fill" x="45.49" y="26" width="1" height="12.27" />
                <rect className="color-2-fill" x="32.81" y="22.02" width="1" height="19.95" />
            </svg>
        </Icon>
    ),
    rhytSorting: ({ active, completed }: ITaskIconProps) => (
        <Icon className={cn({ ActiveIcon: active, CompletedIcon: completed })}>
            <svg width="64" height="64" viewBox="0 0 64 64">
                <g>
                    <rect x="12" y="15" width="41" height="32" rx="2.3" ry="2.3" className={"color-1-fill"} />
                    <path
                        className={"color-2-fill"}
                        d="M25.5,35.7a1.5,1.5,0,0,1,1.7,1.4,2.7,2.7,0,0,1-1.6,2.3,3.2,3.2,0,0,1-1.8.6c-.9,0-1.7-.4-1.7-1.4a2.9,2.9,0,0,1,1.6-2.3A3.9,3.9,0,0,1,25.5,35.7Z"
                    />
                    <path
                        className={"color-2-fill"}
                        data-name="path78"
                        d="M39.1,35.7a1.5,1.5,0,0,1,1.6,1.4,2.6,2.6,0,0,1-1.5,2.3,3.3,3.3,0,0,1-1.9.6,1.4,1.4,0,0,1-1.6-1.4,2.7,2.7,0,0,1,1.6-2.3A3.5,3.5,0,0,1,39.1,35.7Z"
                    />
                    <rect className={"color-2-fill"} x="27" y="22" width="13.6" height="2.75" />
                    <rect className={"color-2-fill"} x="40.1" y="22" width="0.6" height="15.38" />
                    <rect className={"color-2-fill"} x="26.5" y="22" width="0.6" height="15.38" />
                </g>
            </svg>
        </Icon>
    ),
    rhytName: ({ active, completed }: ITaskIconProps) => (
        <Icon className={cn({ ActiveIcon: active, CompletedIcon: completed })}>
            <svg width="64" height="64" viewBox="0 0 64 64">
                <g>
                    <rect x="32" y="28" width="32" height="4" className={"color-2-fill"} />
                    <g transform="translate(32 32)">
                        <rect x="0" y="-2" width="32" height="4" transform="rotate(-60)" className={"color-2-fill"} />
                    </g>
                    <circle cx="32" cy="32" r="18" className={"color-1-fill"} />
                    <g transform="translate(22 40)" style={{ fontSize: 24 }}>
                        <text className={"color-2-fill"}>
                            <Ta />
                        </text>
                    </g>
                </g>
            </svg>
        </Icon>
    ),
    rhytKnock: ({ active, completed }: ITaskIconProps) => (
        <Icon className={cn({ ActiveIcon: active, CompletedIcon: completed })}>
            <svg width="64" height="64" viewBox="0 0 64 64">
                <polygon className="color-1-fill" points="16.22 37.73 23.19 46.28 16.22 46.28 16.22 37.73" />
                <polygon className="color-1-fill" points="31.4 35.42 24 44.52 16.59 35.42 31.4 35.42" />
                <polygon className="color-1-fill" points="32.21 37.2 39.62 46.28 24.81 46.28 32.21 37.2" />
                <polygon className="color-1-fill" points="47.41 35.42 40.21 44.26 33.02 35.42 47.41 35.42" />
                <polygon className="color-1-fill" points="47.78 37.73 47.78 46.28 40.81 46.28 47.78 37.73" />
                <rect className="color-1-fill" x="13.89" y="28.1" width="36.22" height="5.71" rx="1.06" />
                <rect className="color-1-fill" x="13.89" y="47.9" width="36.22" height="5.71" rx="1.06" />
                <path
                    className="color-2-fill"
                    d="M51.88,15.11a7.45,7.45,0,0,1-1.94,1.21L48,17.41l-3.89,2.21c-2.35,1.33-4.66,2.75-7,4h0a2,2,0,1,1-1.88-2.58,1.92,1.92,0,0,1,.59.09L39.17,19l7.41-4.82L48.41,13a3.94,3.94,0,0,1,2.15-1A1.9,1.9,0,0,1,51.88,15.11Z"
                />
                <path
                    className="color-2-fill"
                    d="M30.75,23a2,2,0,0,1-3.87.59c-2.38-1.26-4.69-2.69-7-4L16,17.41l-1.91-1.09a7.56,7.56,0,0,1-1.93-1.21A1.9,1.9,0,0,1,13.44,12a3.94,3.94,0,0,1,2.15,1l1.83,1.19L24.83,19l3.35,2.18a1.92,1.92,0,0,1,.59-.09A2,2,0,0,1,30.75,23Z"
                />
            </svg>
        </Icon>
    ),
    rhytNotation: ({ active, completed }: ITaskIconProps) => (
        <Icon className={cn({ ActiveIcon: active, CompletedIcon: completed })}>
            <svg width="64" height="64" viewBox="0 0 64 64">
                <circle cx="32" cy="33" r="18" className={"color-1-fill"} />
                <g transform="translate(19 -2)">
                    <path
                        className={"color-2-fill"}
                        d="M18.1,36.2c0,0-0.1,0.3-0.3,0.3c-0.2,0-0.1-0.1,0-0.3c0.1-0.2,2.7-5.6-3.2-7.5h-0.1l-0.1,12.1h-0.2c0,1-0.8,2.1-2.2,2.8
            			c-1.7,0.9-3.6,0.7-4.2-0.4c-0.6-1.1,0.3-2.6,2-3.5c1.5-0.8,3.2-0.7,4,0.1L13.8,24h0.7c0,0,0.1,1,1.7,3.5
            			C17.8,30.1,20.5,31.5,18.1,36.2z"
                    />
                </g>
            </svg>
        </Icon>
    ),
    solfaName: ({ active, completed }: ITaskIconProps) => {
        const { locale } = useIntl();
        const { solfaShort = false } = useUser();
        return (
            <Icon className={cn({ ActiveIcon: active, CompletedIcon: completed })}>
                <svg width="64" height="64" viewBox="0 0 64 64">
                    <g>
                        <rect x="0" y="28" width="64" height="4" className={"color-2-fill"} />
                        <circle cx="32" cy="32" r="18" className={"color-1-fill"} />
                        <g transform="translate(32 40)" style={{ fontSize: 20 }}>
                            <text className={"color-2-fill"} style={{ textAnchor: "middle" }}>
                                {lengthenSolfa("s", locale, solfaShort)}
                            </text>
                        </g>
                    </g>
                </svg>
            </Icon>
        );
    },
    handSign: ({ active, completed }: ITaskIconProps) => (
        <Icon className={cn({ ActiveIcon: active, CompletedIcon: completed })}>
            <svg width="64" height="64" viewBox="0 0 64 64">
                <g>
                    <g>
                        <line
                            className="color-2-stroke"
                            x1="6.3"
                            y1="11.2"
                            x2="58"
                            y2="11.2"
                            style={{ strokeWidth: 1.5 }}
                        />
                        <line
                            className="color-2-stroke"
                            x1="-0.5"
                            y1="32.2"
                            x2="64.8"
                            y2="32.2"
                            style={{ strokeWidth: 1.5 }}
                        />
                        <line
                            className="color-2-stroke"
                            x1="6.3"
                            y1="53.2"
                            x2="58"
                            y2="53.2"
                            style={{ strokeWidth: 1.5 }}
                        />
                    </g>
                    <g>
                        <path
                            className="color-1-fill"
                            d="M20.6,37.7h6.9v1.2H17a2.8,2.8,0,0,0-1.8.9,1.8,1.8,0,0,0-.4,1.4A1.9,1.9,0,0,0,17,42.5h6.4c4.4.1,8.9.1,11.1-.1a15.8,15.8,0,0,0,3.7-.8,14,14,0,0,1,3.5-.8,30.8,30.8,0,0,1,6.8-.1,47.2,47.2,0,0,1-.3-8.4,38.6,38.6,0,0,1,.6-4.9,17,17,0,0,1-5.4-1.4,27.3,27.3,0,0,1-3.5-1.9l-.7-.5a16.5,16.5,0,0,1-4.3-4.6,20.3,20.3,0,0,1-1.3-2.2c-.6-1-.9-1.5-1.3-1.6a4.8,4.8,0,0,0-1.8.8c-.7.7-.1,4.2,2.1,8.7a32.8,32.8,0,0,0,6.8,9.1l-.8.9a32.4,32.4,0,0,1-7.1-9.5l-.4-.8H15.5a2.1,2.1,0,0,0-2.2,1.3,1.8,1.8,0,0,0,.4,1.4,2.4,2.4,0,0,0,1.8.9H27.6v1.2H10.1a2.1,2.1,0,0,0-2.2,1.4A1.4,1.4,0,0,0,8.3,32a2.4,2.4,0,0,0,1.8.9H27.6v1.2H10.1a2.1,2.1,0,0,0-2.2,1.4,1.4,1.4,0,0,0,.4,1.3,2.1,2.1,0,0,0,1.8.9Z"
                        />
                        <path
                            className="color-1-fill"
                            d="M55.9,43.3a48.7,48.7,0,0,1-.8-11.4,43.6,43.6,0,0,1,.7-6.2H50.4a38.5,38.5,0,0,0-.9,6.7A44.3,44.3,0,0,0,50,43.3Z"
                        />
                        <path
                            className="color-2-fill"
                            d="M14.3,38.9c0,.1,0,.1-.1.2a2.8,2.8,0,0,0-.6,2.3A3.4,3.4,0,0,0,17,43.8H29.8a31.8,31.8,0,0,0,4.8-.2,12.5,12.5,0,0,0,4-.9,13.8,13.8,0,0,1,3.2-.7,31,31,0,0,1,6.8-.1l.3,2v.5h8.4l-.2-.7A47,47,0,0,1,56.3,32a50.5,50.5,0,0,1,.8-6.8l.2-.7H49.4v.5L49,26.2A13.1,13.1,0,0,1,43.9,25a23.8,23.8,0,0,1-3.3-1.8l-.8-.5a14.1,14.1,0,0,1-3.9-4.3,13.5,13.5,0,0,1-1.3-2.1c-.7-1.2-1.2-2.1-2.3-2.2h-.2a5.8,5.8,0,0,0-2.4,1.1c-1.6,1.4-.2,5.4.9,8.1H15.5a3.2,3.2,0,0,0-3.4,2.3,3.2,3.2,0,0,0,.6,2.3l.2.2H10.1a3.2,3.2,0,0,0-3.4,2.3,2.8,2.8,0,0,0,.6,2.4l.7.6a2.8,2.8,0,0,0-1.3,1.8,2.7,2.7,0,0,0,.6,2.4,3.7,3.7,0,0,0,2.8,1.3Zm35.2-6.5a38.5,38.5,0,0,1,.9-6.7h5.4a43.6,43.6,0,0,0-.7,6.2,48.7,48.7,0,0,0,.8,11.4H50A44.3,44.3,0,0,1,49.5,32.4ZM8.3,36.8a1.4,1.4,0,0,1-.4-1.3,2.1,2.1,0,0,1,2.2-1.4H27.6V32.9H10.1A2.4,2.4,0,0,1,8.3,32a1.4,1.4,0,0,1-.4-1.3,2.1,2.1,0,0,1,2.2-1.4H27.6V28.1H15.5a2.4,2.4,0,0,1-1.8-.9,1.8,1.8,0,0,1-.4-1.4,2.1,2.1,0,0,1,2.2-1.3H31.1l.4.8a32.4,32.4,0,0,0,7.1,9.5l.8-.9a32.8,32.8,0,0,1-6.8-9.1c-2.2-4.5-2.8-8-2.1-8.7a4.8,4.8,0,0,1,1.8-.8c.4.1.7.6,1.3,1.6a20.3,20.3,0,0,0,1.3,2.2,16.5,16.5,0,0,0,4.3,4.6l.7.5a27.3,27.3,0,0,0,3.5,1.9,17,17,0,0,0,5.4,1.4,38.6,38.6,0,0,0-.6,4.9,47.2,47.2,0,0,0,.3,8.4,30.8,30.8,0,0,0-6.8.1,14,14,0,0,0-3.5.8,15.8,15.8,0,0,1-3.7.8c-2.2.2-6.7.2-11.1.1H17a1.9,1.9,0,0,1-2.2-1.3,1.8,1.8,0,0,1,.4-1.4,2.8,2.8,0,0,1,1.8-.9H27.5V37.7H10.1A2.1,2.1,0,0,1,8.3,36.8Z"
                        />
                    </g>
                </g>
            </svg>
        </Icon>
    ),
    intervals: ({ active, completed }: ITaskIconProps) => (
        <Icon className={cn({ ActiveIcon: active, CompletedIcon: completed })}>
            <svg width="64" height="64" viewBox="0 0 64 64">
                <rect className="color-2-fill" x="6.14" y="10.26" width="51.72" height="1" />
                <rect className="color-2-fill" x="-0.67" y="33.3" width="65.34" height="1" />
                <rect className="color-2-fill" x="0.71" y="21.78" width="62.58" height="1" />
                <rect className="color-2-fill" x="8.9" y="56.34" width="46.21" height="1" />
                <rect className="color-2-fill" x="2" y="44.82" width="60" height="1" />
                <path
                    className="color-1-fill"
                    d="M22.72,49.67c-2.55-.14-4.55-2.41-5.35-4.75-.48-1.37-.22-3.44,1.4-3.82,2.34-.4,4.28,1.59,5.37,3.49.78,1.41,1.21,3.63-.25,4.75A2.09,2.09,0,0,1,22.72,49.67Zm4.07-8a12,12,0,0,0-9.66-.9c-2,.69-4.28,2.18-4.3,4.59S15,49.17,17,49.87a12.07,12.07,0,0,0,9.53-.69c1.63-.87,3.07-2.66,2.58-4.65A4.77,4.77,0,0,0,26.79,41.63Z"
                />
                <path
                    className="color-1-fill"
                    d="M22.73,26.63c-2.55-.14-4.55-2.41-5.35-4.75-.49-1.37-.22-3.44,1.4-3.82,2.34-.4,4.28,1.59,5.37,3.49.77,1.41,1.2,3.63-.25,4.75A2.12,2.12,0,0,1,22.73,26.63Zm4.07-8a12,12,0,0,0-9.66-.9c-2,.69-4.28,2.18-4.3,4.59S15,26.13,17,26.83a12.05,12.05,0,0,0,9.52-.69c1.63-.87,3.08-2.66,2.59-4.65A4.77,4.77,0,0,0,26.8,18.59Z"
                />
                <polygon
                    className="color-1-fill"
                    points="52.66 20.81 52.66 46.8 42.74 46.8 42.74 49.78 34.96 45.3 42.74 40.81 42.74 43.8 49.66 43.8 49.66 23.81 42.74 23.81 42.74 26.8 34.96 22.31 42.74 17.82 42.74 20.81 52.66 20.81"
                />
            </svg>
        </Icon>
    ),
    chords3: ({ active, completed }: ITaskIconProps) => (
        <Icon className={cn({ ActiveIcon: active, CompletedIcon: completed })}>
            <svg width="64" height="64" viewBox="0 0 64 64">
                <rect className="color-2-fill" x="6.14" y="10.26" width="51.72" height="1" />
                <rect className="color-2-fill" x="-0.67" y="33.3" width="65.34" height="1" />
                <rect className="color-2-fill" x="0.71" y="21.78" width="62.58" height="1" />
                <rect className="color-2-fill" x="8.9" y="56.34" width="46.21" height="1" />
                <rect className="color-2-fill" x="2" y="44.82" width="60" height="1" />
                <path
                    className="color-1-fill"
                    d="M22.73,49.67c-2.56-.14-4.55-2.41-5.35-4.75-.49-1.37-.22-3.44,1.39-3.82,2.35-.4,4.29,1.59,5.38,3.49.77,1.41,1.2,3.63-.25,4.75A2.14,2.14,0,0,1,22.73,49.67Zm4.06-8a12,12,0,0,0-9.66-.9c-2,.69-4.27,2.18-4.29,4.59S15,49.17,17,49.87a12.05,12.05,0,0,0,9.52-.69c1.63-.87,3.08-2.66,2.59-4.65A4.75,4.75,0,0,0,26.79,41.63Z"
                />
                <path
                    className="color-1-fill"
                    d="M22.73,26.63c-2.56-.14-4.55-2.41-5.35-4.75-.49-1.37-.22-3.44,1.39-3.82,2.35-.4,4.29,1.59,5.38,3.49.77,1.41,1.2,3.63-.25,4.75A2.14,2.14,0,0,1,22.73,26.63Zm4.06-8a12,12,0,0,0-9.66-.9c-2,.69-4.27,2.18-4.29,4.59S15,26.13,17,26.83a12.05,12.05,0,0,0,9.52-.69c1.63-.87,3.08-2.66,2.59-4.65A4.75,4.75,0,0,0,26.79,18.59Z"
                />
                <polygon
                    className="color-1-fill"
                    points="52.67 20.81 52.67 46.8 42.74 46.8 42.74 49.79 34.97 45.3 42.74 40.81 42.74 43.8 49.67 43.8 49.67 35.31 42.74 35.31 42.74 38.29 34.97 33.81 42.74 29.32 42.74 32.31 49.67 32.31 49.67 23.81 42.74 23.81 42.74 26.8 34.97 22.31 42.74 17.83 42.74 20.81 52.67 20.81"
                />
                <path
                    className="color-1-fill"
                    d="M22.73,38.15c-2.56-.14-4.55-2.41-5.35-4.75-.49-1.37-.22-3.44,1.39-3.82,2.35-.4,4.29,1.59,5.38,3.49.77,1.41,1.2,3.63-.25,4.75A2.14,2.14,0,0,1,22.73,38.15Zm4.06-8a12,12,0,0,0-9.66-.9c-2,.69-4.27,2.18-4.29,4.59S15,37.65,17,38.35a12.05,12.05,0,0,0,9.52-.69C28.14,36.79,29.59,35,29.1,33A4.75,4.75,0,0,0,26.79,30.11Z"
                />
            </svg>
        </Icon>
    ),
    piano: ({ active, completed }: ITaskIconProps) => (
        <Icon className={cn({ ActiveIcon: active, CompletedIcon: completed })}>
            <svg viewBox="0 0 64 64" width="64" height="64">
                <path
                    className={"color-1-fill"}
                    d="M36.9,52.8H27c-1.1,0-2.1-0.9-2.1-2.1v-37c0-1.1,0.9-2.1,2.1-2.1h9.9c1.1,0,2.1,0.9,2.1,2.1v37C39,51.9,38.1,52.8,36.9,52.8z"
                />
                <path
                    className={"color-1-fill"}
                    d="M51.9,52.8h-9.9c-1.1,0-2.1-0.9-2.1-2.1v-37c0-1.1,0.9-2.1,2.1-2.1h9.9c1.1,0,2.1,0.9,2.1,2.1v37C54,51.9,53.1,52.8,51.9,52.8z"
                />
                <path
                    className={"color-1-fill"}
                    d="M21.9,52.8H12c-1.1,0-2.1-0.9-2.1-2.1v-37c0-1.1,0.9-2.1,2.1-2.1h9.9c1.1,0,2.1,0.9,2.1,2.1v37C23.9,51.9,23,52.8,21.9,52.8z"
                />
                <path
                    className={"color-2-fill"}
                    d="M26.7,38.6h-4.6c-1.1,0-2.1-0.9-2.1-2.1V11.8h8.7v24.8C28.8,37.7,27.9,38.6,26.7,38.6z"
                />
                <path
                    className={"color-2-fill"}
                    d="M41.8,38.6h-4.6c-1.1,0-2.1-0.9-2.1-2.1V11.8h8.7v24.8C43.9,37.7,43,38.6,41.8,38.6z"
                />
            </svg>
        </Icon>
    ),
    melodyTable: ({ active, completed }: ITaskIconProps) => (
        <Icon className={cn({ ActiveIcon: active, CompletedIcon: completed })}>
            <svg width="64" height="64" viewBox="0 0 64 64">
                <g>
                    <line
                        x1="-0.5"
                        y1="42.5"
                        x2="64.5"
                        y2="42.5"
                        className={"color-2-stroke"}
                        style={{ fill: "none", strokeMiterlimit: 10, strokeWidth: 2 }}
                    />
                    <line
                        y1="22.5"
                        x2="64.5"
                        y2="22.5"
                        className={"color-2-stroke"}
                        style={{ fill: "none", strokeMiterlimit: 10, strokeWidth: 2 }}
                    />
                    <rect x="9" y="27" width="22" height="11" rx="2.1" ry="2.1" className={"color-1-fill"} />
                    <rect x="34" y="17" width="22" height="11" rx="2" ry="2" className={"color-1-fill"} />
                </g>
            </svg>
        </Icon>
    ),
    tonesetStaff: ({ active, completed }: ITaskIconProps) => (
        <Icon className={cn({ ActiveIcon: active, CompletedIcon: completed })}>
            <svg width="64" height="64" viewBox="0 0 64 64">
                <rect className="color-2-fill" x="-0.2" y="21.4" width="65.3" height="2" />
                <rect className="color-2-fill" x="-1" y="40.9" width="66.2" height="2" />
                <path className="color-1-fill" d="M49.9,17.1c2.5,0,4.8,1.3,4.8,4s-2.4,5.3-4.5,6.6a10.4,10.4,0,0,1-5.1,1.5c-2.5,0-4.8-1.2-4.8-4s2.4-5.3,4.5-6.5A10.5,10.5,0,0,1,49.9,17.1Z" />
                <path className="color-1-fill" d="M16.9,29.7A6.8,6.8,0,0,0,18.3,25V22a4.4,4.4,0,0,1,1-3.2,4.1,4.1,0,0,1,3-1h1.4V14.5H22a8,8,0,0,0-5.4,1.8,7,7,0,0,0-2,5.4v2.6c0,3.9-1.6,5.7-4.7,5.7H9.1v3.7h.8A4.3,4.3,0,0,1,13.4,35a7,7,0,0,1,1.2,4.4V42a6.9,6.9,0,0,0,2,5.4A7.8,7.8,0,0,0,22,49.2h1.7V45.9H22.3a3.8,3.8,0,0,1-3-1,4.2,4.2,0,0,1-1-3.2v-3a7,7,0,0,0-4.1-6.8A8.4,8.4,0,0,0,16.9,29.7Z" />
                <path className="color-1-fill" d="M33.8,27.2c2.5,0,4.8,1.3,4.8,4s-2.5,5.3-4.5,6.6a10.9,10.9,0,0,1-5.2,1.6c-2.5,0-4.8-1.3-4.8-4.1s2.5-5.3,4.5-6.5A10.9,10.9,0,0,1,33.8,27.2Z" />
            </svg>
        </Icon>
    ),
    toneset: ({ active, completed }: ITaskIconProps) => (
        <Icon className={cn({ ActiveIcon: active, CompletedIcon: completed })}>
            <svg width="64" height="64" viewBox="0 0 64 64">
                <rect className="color-2-fill" x="35.5" y="20.7" width="21.2" height="10.04" rx="1.9" />
                <rect className="color-2-fill" x="23.7" y="33.9" width="21.2" height="10.04" rx="1.9" />
                <path className="color-1-fill" d="M15.9,30.6a7.7,7.7,0,0,0,1.4-4.7V22.8a4.4,4.4,0,0,1,.9-3.1,4.1,4.1,0,0,1,3.1-1h1.4V15.3H21a7.7,7.7,0,0,0-5.4,1.8,7,7,0,0,0-2.1,5.4v2.6c0,3.9-1.5,5.8-4.6,5.8H8v3.6h.9a4.3,4.3,0,0,1,3.4,1.4,6.6,6.6,0,0,1,1.2,4.4v2.6a6.6,6.6,0,0,0,2.1,5.3A7.3,7.3,0,0,0,21,50.1h1.7V46.7H21.3a4.6,4.6,0,0,1-3.1-.9,4.9,4.9,0,0,1-.9-3.2v-3a7,7,0,0,0-4.1-6.9A7.1,7.1,0,0,0,15.9,30.6Z" />
            </svg>
        </Icon>
    ),
    embed: ({ active, completed }: ITaskIconProps) => (
        <Icon className={cn({ ActiveIcon: active, CompletedIcon: completed })}>
            <svg width="64" height="64" viewBox="0 0 64 64">
                <g>
                    <path
                        className={"color-1-fill"}
                        d="M32.4,49c-6.1,0-12-.6-17.9-1-3.7-.3-5.5-2.2-6-5.9a65.8,65.8,0,0,1,0-20.3c.6-4.2,2.7-5.7,6.9-6.3a63.1,63.1,0,0,1,9.5-.5c8.3.2,17.1,0,24.9.8,3.2.3,5,2,5.6,5.2a65.3,65.3,0,0,1,0,21.6c-.6,3.4-2.6,5.2-6.1,5.5C43.6,48.5,37.9,49,32.4,49Zm-5.2-9.9,12.7-7.3L27.2,24.5Z"
                    />
                    <path className={"color-2-fill"} d="M27.2,39.1V24.5l12.7,7.3Z" />
                </g>
            </svg>
        </Icon>
    ),
    subScore: ({ active, completed }: ITaskIconProps) => (
        <Icon className={cn({ ActiveIcon: active, CompletedIcon: completed })}>
            <svg width="64" height="64" viewBox="0 0 64 64">
                <g>
                    <path className={"color-1-fill"} d="M55.4,42.6c-0.6,3.4-2.6,5.2-6.1,5.5C43.6,48.5,37.9,49,32.4,49c-6.1,0-12-0.6-17.9-1c-3.7-0.3-5.5-2.2-6-5.9
                        c-1-6.7-1-13.6,0-20.3c0.6-4.2,2.7-5.7,6.9-6.3c3.1-0.4,6.3-0.6,9.5-0.5c8.3,0.2,17.1,0,24.9,0.8c3.2,0.3,5,2,5.6,5.2
                        C56.6,28.1,56.6,35.4,55.4,42.6z"/>
                    <g transform="translate(19 -2)">
                        <path className={"color-2-fill"} d="M17.8,36.1c0,0-0.1,0.3-0.3,0.3c-0.2,0-0.1-0.1,0-0.3s2.7-5.6-3.2-7.5h-0.1l-0.1,12.1h-0.2c0,1-0.8,2.1-2.2,2.8
                            c-1.7,0.9-3.6,0.7-4.2-0.4c-0.6-1.1,0.3-2.6,2-3.5c1.5-0.8,3.2-0.7,4,0.1l0-15.8h0.7c0,0,0.1,1,1.7,3.5
                            C17.5,30,20.2,31.4,17.8,36.1z"/>
                    </g>
                </g>
            </svg>
        </Icon>
    ),
    flow: ({ active, completed }: ITaskIconProps) => (
        <Icon className={cn({ ActiveIcon: active, CompletedIcon: completed })}>
            <svg width="64" height="64" viewBox="0 0 64 64">
                <path className="color-2-fill" d="M57.76,32l-7.38-4.87V25.05l8.71,6V33l-8.71,6V36.88Z" />
                <path className="color-2-fill" d="M6.85,32l7.38,4.87V39L5.51,33V31l8.72-6v2.07Z" />
                <rect className="color-1-fill" x="18.91" y="16.64" width="27.54" height="30.72" rx="2.3" />
                <path
                    className="color-2-fill"
                    d="M32.93,20.29h.32a.25.25,0,0,1,.25.24V39.72a.25.25,0,0,1-.25.24h-.32a.24.24,0,0,1-.24-.24V20.53A.24.24,0,0,1,32.93,20.29Z"
                />
                <path
                    className="color-2-fill"
                    d="M31.26,37.69c1.15,0,2.23.6,2.23,1.86a3.74,3.74,0,0,1-2.1,3.05,4.86,4.86,0,0,1-2.4.74c-1.14,0-2.23-.6-2.23-1.86a3.75,3.75,0,0,1,2.11-3A4.85,4.85,0,0,1,31.26,37.69Z"
                />
                <path
                    className="color-2-fill"
                    d="M33.1,20.35h.47a12,12,0,0,0,2,4.18c1.91,3.12,5.14,4.79,2.27,10.42,0,0-.12.36-.36.36s-.24-.12-.12-.36,3.24-6.71-3.83-9H33.1Z"
                />
            </svg>
        </Icon>
    ),
    singing: ({ active, completed }: ITaskIconProps) => (
        <Icon className={cn({ ActiveIcon: active, CompletedIcon: completed })}>
            <svg width="64" height="64" viewBox="0 0 64 64">
                <rect className="color-1-fill" x="23.66" y="9.16" width="16.67" height="33.61" rx="7.6" />
                <path
                    className="color-2-fill"
                    d="M33,48.1v5.74h8.28v2H22.71v-2H31V48.1A13.43,13.43,0,0,1,18.58,34.73h2a11.42,11.42,0,0,0,22.83,0h2A13.43,13.43,0,0,1,33,48.1Z"
                />
                <rect className="color-2-fill" x="27.11" y="24.96" width="10.01" height="2" />
                <rect className="color-2-fill" x="27.11" y="28.54" width="10.01" height="2" />
                <rect className="color-2-fill" x="27.11" y="21.39" width="10.01" height="2" />
            </svg>
        </Icon>
    ),
    fancyArrow: ({ reverse = false }) => (
        <svg width="50" viewBox="0 0 94 128">
            <g transform={"rotate(" + (reverse ? 180 : 0) + ")"} style={{ transformOrigin: "center center" }}>
                <path d="M69.7,123.5c-0.4-0.7-0.8-1.4-1.1-2.1C69,122.2,69.4,123,69.7,123.5z" />
                <path
                    d="M58.3,88.6c1.6,13.9,6.3,25.3,10.2,32.7c-1.8-3-5.2-7.5-10.9-10.4c-12.9-6.5-26,0.8-27,1.4C31.1,98,33,87.4,34.8,80.1
                    c0-0.1,2.1-8.5,1.9-13.8c-13.3,1.7-23.3,6.1-28.7,8.8c6.7-5.1,16.6-14,25-27.5C43,31.3,46.2,15.8,47.3,6.8c0,7.2,1.1,23,10.9,39.8
                    c9.5,16.2,22.3,24.8,28.5,28.4c-5.6-2.9-15.3-7.1-28.1-8.8C56.9,76.1,57.8,84,58.3,88.6z"
                />
            </g>
        </svg>
    ),
    darkMode: () => (
        <svg viewBox="0 0 64 64" width="23" height="23"><path d="M60.1,41.2a.8.8,0,0,0-.8,0A26.4,26.4,0,0,1,22.8,4.7a.8.8,0,0,0,0-.8.8.8,0,0,0-.8-.2,30.1,30.1,0,0,0-9.7,6.5A29.3,29.3,0,1,0,53.8,51.7,30.1,30.1,0,0,0,60.3,42,.8.8,0,0,0,60.1,41.2Z"/></svg>
    )
};

const Ta = () => {
    const { locale } = useIntl();
    return (
        <>t{locale === "hu" ? "á" : "a"}</>
    );
};

export default Icons;